import classnames from 'classnames/bind';
import Button from 'components/button';
import Wrapper from 'components/wrapper';
import { PATH } from 'config';
import logo from 'images/icon-pnc-blue.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import repo from 'service/repository';
import styles from '../aktzin/index.module.sass';

const cx = classnames.bind(styles);

const QrScannerPage = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [usingHtml5, setUsingHtml5] = useState(false);
    // scanResult removed - UI simplified
    const html5QrCodeRef = useRef(null);
    const html5LoadPromiseRef = useRef(null);
    const isMountedRef = useRef(true);

    // 動態載入 html5-qrcode 庫（public/assets/scripts/html5-qrcode.min.js）
    const loadHtml5QrLib = useCallback(() => {
        if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
        if (window.Html5Qrcode) return Promise.resolve();
        if (html5LoadPromiseRef.current) return html5LoadPromiseRef.current;

        const p = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/assets/scripts/html5-qrcode.min.js';
            script.async = true;
            script.onload = () => {
                console.log('html5-qrcode loaded');
                resolve();
            };
            script.onerror = (e) => reject(new Error('failed to load html5-qrcode'));
            document.head.appendChild(script);
        });

        html5LoadPromiseRef.current = p;
        return p;
    }, []);
    const [chargerId, setChargerId] = useState('');

    // 自動重啟掃描函數
    const restartScanning = useCallback(() => {
        if (!isMountedRef.current) return; // 組件已卸載則不執行
        setLoading(false);
        setTimeout(() => {
            if (!isMountedRef.current) return; // 再次檢查
            // 重置狀態並觸發重新渲染
            setIsScanning(false);
            setUsingHtml5(false);
            setError('');

            // 短暫延遲後重新啟動
            setTimeout(() => {
                if (!isMountedRef.current) return; // 再次檢查
                setIsScanning(true);
            }, 200);
        }, 1500);
    }, []);

    // 關閉相機函數
    const stopCamera = useCallback(async () => {
        try {
            if (html5QrCodeRef.current) {
                await html5QrCodeRef.current.stop();
                html5QrCodeRef.current.clear();
                html5QrCodeRef.current = null;
            }
            setIsScanning(false);
            setUsingHtml5(false);
        } catch (e) {
            console.warn('停止相機失敗:', e);
        }
    }, []);

    // QR Code 處理函數
    const processQrCode = useCallback(async (qrCodeText) => {
        console.log('掃描到 QR Code:', qrCodeText);
        console.log('QR Code 長度:', qrCodeText.length);
        console.log('QR Code 類型:', typeof qrCodeText);
        console.log('QR Code 字節:', Array.from(qrCodeText).map(c => c.charCodeAt(0)));
        
        setLoading(true);
        setError('');
        setIsScanning(false); // 停止掃描以處理 QR Code

        try {
            // 1. Pick 'n Charge QR Code 檢測
            if (qrCodeText.includes('pickncharge') || qrCodeText.includes('pnc')) {
                console.log('偵測到 Pick n Charge QR Code');
                // 關閉相機後跳轉
                await stopCamera();
                // 直接跳轉到掃描的完整 URL
                window.location.href = qrCodeText;
                return;
            }

            // 2. 區快充 (CBLOK) QR Code 檢測
            const cblokMatch = qrCodeText.match(/TW\*CBK\*(\d{5})/i);
            if (cblokMatch) {
                const stationId = cblokMatch[1];
                console.log('偵測到區快充 QR Code, Station ID:', stationId);

                try {
                    const [, data] = await repo.getEvsesByChargeBoxId(`TW*CBK*${stationId}`);
                    if (data) {
                        // 關閉相機後跳轉
                        await stopCamera();
                        history.push({
                            pathname: `${PATH.EVSES}/${data.serialNumber}`
                        });
                        return;
                    } else {
                        setError('充電樁槍號不存在，請確認後重新輸入');
                        // 重新啟動掃描
                        restartScanning();
                        return;
                    }
                } catch (err) {
                    console.error('無法取得區快充站點資訊:', err);
                    setError('無法取得充電站資訊，請重新掃描或手動輸入');
                    // 重新啟動掃描
                    restartScanning();
                    return;
                }
            }

            // 3. 連展 (ACON) QR Code 檢測
            // 規則：9碼純數字（如 113011113）
            // 增加更寬鬆的匹配，處理可能的空白字符和編碼問題
            const cleanedText = qrCodeText.replace(/\s+/g, '').replace(/[^\d]/g, '');
            console.log('清理後的文字:', cleanedText);
            
            const aconMatch = cleanedText.match(/^(\d{9})$/);
            if (aconMatch) {
                const chargeBoxId = aconMatch[1];
                console.log('偵測到連展 QR Code, ChargeBox ID:', chargeBoxId);
                console.log('原始 QR 文字:', JSON.stringify(qrCodeText));
                console.log('清理後文字:', JSON.stringify(cleanedText));

                try {
                    const [, data] = await repo.getEvsesByChargeBoxId(chargeBoxId);
                    if (data) {
                        // 關閉相機後跳轉
                        await stopCamera();
                        history.push({
                            pathname: `${PATH.EVSES}/${data.serialNumber}`
                        });
                        return; // 重要：防止繼續執行後續步驟
                    } else {
                        // 查詢成功但沒有資料，顯示錯誤訊息並停止處理
                        if (isMountedRef.current) {
                            setError('充電樁槍號不存在，請確認後重新輸入');
                            // 重新啟動掃描
                            restartScanning();
                        }
                        return;
                    }
                } catch (err) {
                    console.error('無法取得連展站點資訊:', err);
                    if (isMountedRef.current) {
                        setError('無法取得充電站資訊，請重新掃描或手動輸入');
                        // 重新啟動掃描
                        restartScanning();
                    }
                    return;
                }
            }

            // 4. 通用數字 QR Code (可能是槍號)
            const gunNumberMatch = qrCodeText.match(/^\d+$/);
            if (gunNumberMatch) {
                const gunNumber = gunNumberMatch[0];
                console.log('偵測到數字 QR Code (槍號):', gunNumber);

                // 嘗試區快充
                try {
                    const [, data] = await repo.getEvsesByChargeBoxId(`TW*CBK*${gunNumber}`);
                    if (data) {
                        // 關閉相機後跳轉
                        await stopCamera();
                        history.push({
                            pathname: `${PATH.EVSES}/${data.serialNumber}`
                        });
                        return;
                    }
                } catch (err) {
                    console.log('非區快充槍號:', err.message);
                }

                // 如果不是區快充，可能是連展
                try {
                    const response = await repo.getEvsesByChargeBoxId(gunNumber);
                    if (response.status === 200 && response.data?.length > 0) {
                        // 關閉相機後跳轉
                        await stopCamera();
                        history.push(PATH.ACON_ENTRY, {
                            chargeBoxId: gunNumber,
                            evses: response.data,
                            qrCode: qrCodeText
                        });
                        return;
                    }
                } catch (err) {
                    console.log('非連展槍號:', err.message);
                }
            }


            try {
                const [, data] = await repo.getEvsesByQRCode(qrCodeText);
                console.log(data);
                if (data) {
                    // 關閉相機後跳轉
                    await stopCamera();
                    history.push({
                        pathname: `${PATH.EVSES}/${data.serialNumber}`
                    });
                    return;
                }
            } catch (err) {
                console.log('非 MappingQrcode:', err.message);
            }

            // 如果所有檢測都失敗
            setError('無法識別的 QR Code 格式，請確認後重新掃描');
            // 重新啟動掃描
            restartScanning();
            return;

        } catch (err) {
            console.error('QR Code 處理錯誤:', err);
            setError('處理 QR Code 時發生錯誤，請重試');
            // 重新啟動掃描
            restartScanning();
            return;
        } finally {
            setLoading(false);
        }
    }, [history, restartScanning, stopCamera]);

    // Html5Qrcode start/stop helpers
    const startHtml5Scanner = useCallback(async () => {
        try {
            if (!window.Html5Qrcode) {
                // try to dynamically load
                try {
                    await loadHtml5QrLib();
                } catch (e) {
                    console.error('loadHtml5QrLib failed', e);
                    setError('無法載入 Html5Qrcode');
                    return;
                }
            }

            setUsingHtml5(true);
            setIsScanning(true);
            setError('');

            const qrRegionId = 'qr-reader';
            // wait for DOM element to appear (react render)
            const waitForElement = async (id, timeout = 3000) => {
                const start = Date.now();
                while (Date.now() - start < timeout) {
                    if (document.getElementById(id)) return document.getElementById(id);
                    // small delay
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise(r => setTimeout(r, 100));
                }
                return null;
            };

            const el = await waitForElement(qrRegionId, 3000);
            if (!el) {
                console.warn('qr-reader element not found after render');
                setError('無法顯示相機畫面，請稍後重試');
                setIsScanning(false);
                setUsingHtml5(false);
                return;
            }
            // ensure previous instance stopped
            if (html5QrCodeRef.current) {
                try { await html5QrCodeRef.current.stop(); } catch (e) { }
                html5QrCodeRef.current.clear();
                html5QrCodeRef.current = null;
            }

            let html5QrCode;
            if (window.Html5Qrcode) {
                console.log('Using Html5Qrcode');
                html5QrCode = new window.Html5Qrcode(qrRegionId);
            } else if (window.Html5QrcodeScanner) {
                console.log('Using Html5QrcodeScanner');
                // Html5QrcodeScanner will render its own UI into the element
                html5QrCode = new window.Html5QrcodeScanner(qrRegionId, { fps: 10, qrbox: 250 });
            } else {
                console.error('Html5Qrcode API not present');
                setError('Html5Qrcode API not present');
                setUsingHtml5(false);
                setIsScanning(false);
                return;
            }
            html5QrCodeRef.current = html5QrCode;

            const config = { 
                fps: 10, 
                qrbox: { width: 250, height: 250 }
            };

            const qrCodeSuccessCallback = async (decodedText, decodedResult) => {
                console.log('Html5Qrcode decoded:', decodedText);
                console.log('Decoded result type:', typeof decodedText);
                console.log('Decoded result length:', decodedText.length);
                console.log('Decoded result (chars):', decodedText.split('').map(c => c.charCodeAt(0)));
                
                // 對於手機，額外清理可能的隱藏字符和空白
                const cleanedText = decodedText.trim().replace(/[\s\n\r\t]/g, '');
                console.log('Cleaned text:', cleanedText);
                console.log('Cleaned text length:', cleanedText.length);
                
                try {
                    await html5QrCode.stop();
                } catch (err) {
                    console.warn('stop() failed', err);
                }
                setIsScanning(false);
                setUsingHtml5(false);
                html5QrCodeRef.current = null;
                processQrCode(cleanedText || decodedText);
            };

            const qrCodeErrorCallback = (errorMessage) => {
                // ignore frequent scan failure messages
            };

            // If the instance exposes start(), use it. Otherwise if it exposes render(), call render(success, error).
            if (typeof html5QrCode.start === 'function') {
                // 簡化的相機設定，只使用基本配置
                const cameraConfig = { facingMode: 'environment' };
                await html5QrCode.start(cameraConfig, config, qrCodeSuccessCallback, qrCodeErrorCallback);
            } else if (typeof html5QrCode.render === 'function') {
                // Html5QrcodeScanner
                html5QrCode.render(qrCodeSuccessCallback, qrCodeErrorCallback);
            } else {
                console.error('Html5Qrcode instance has no start or render');
                setError('掃描器尚未支援的 API');
                setIsScanning(false);
                setUsingHtml5(false);
            }
        } catch (err) {
            console.error('startHtml5Scanner error', err);
            setError('啟動相機失敗，無法使用 Html5Qrcode');
            setIsScanning(false);
            setUsingHtml5(false);
        }
    }, [processQrCode, loadHtml5QrLib]);

    const stopHtml5Scanner = useCallback(async () => {
        if (html5QrCodeRef.current) {
            try {
                await html5QrCodeRef.current.stop();
            } catch (err) {
                console.warn('stopHtml5Scanner stop() failed', err);
            }
            try { html5QrCodeRef.current.clear(); } catch (e) { }
            html5QrCodeRef.current = null;
        }
        setIsScanning(false);
        setUsingHtml5(false);
    }, []);

    // cleanup on unmount - 移除自動啟動掃描
    useEffect(() => {
        return () => {
            isMountedRef.current = false; // 標記組件已卸載
            try { stopHtml5Scanner(); } catch (e) { }
        };
    }, [stopHtml5Scanner]);

    // 監控 isScanning 狀態變化，自動啟動掃描器
    useEffect(() => {
        if (isScanning && !loading) {
            const startScanner = async () => {
                setError('');
                if (typeof window !== 'undefined' && (window.Html5Qrcode || window.Html5QrcodeScanner)) {
                    startHtml5Scanner();
                } else {
                    // 嘗試載入 Html5Qrcode
                    try {
                        await loadHtml5QrLib();
                        if (window.Html5Qrcode || window.Html5QrcodeScanner) {
                            startHtml5Scanner();
                        } else {
                            // Html5Qrcode 載入失敗
                            console.error('Html5Qrcode 不可用');
                            setError('掃描器載入失敗，請重新整理頁面');
                        }
                    } catch (e) {
                        console.error('無法載入掃描器:', e);
                        setError('無法啟動掃描器，請重試');
                    }
                }
            };

            startScanner();
        }
    }, [isScanning, loading, startHtml5Scanner, loadHtml5QrLib]);

    // 點擊啟動/停止相機
    const toggleCamera = useCallback(async () => {
        if (isScanning) {
            // 停止掃描
            await stopHtml5Scanner();
        } else {
            // 啟動掃描
            setError('');
            if (typeof window !== 'undefined' && (window.Html5Qrcode || window.Html5QrcodeScanner)) {
                startHtml5Scanner();
            } else {
                // 嘗試載入 Html5Qrcode
                try {
                    await loadHtml5QrLib();
                    if (window.Html5Qrcode || window.Html5QrcodeScanner) {
                        startHtml5Scanner();
                    } else {
                        // Html5Qrcode 載入失敗
                        setError('無法載入掃描器，請重新整理頁面');
                    }
                } catch (e) {
                    console.error('無法載入掃描器:', e);
                    setError('無法啟動掃描器，請重試');
                }
            }
        }
    }, [isScanning, stopHtml5Scanner, startHtml5Scanner, loadHtml5QrLib]);

    // 手動輸入功能已移除（UI 精簡）

    // 透過輸入欄位連接充電槍（或以輸入的編號作為 QR 處理）
    const connectCharging = useCallback(() => {
        const id = chargerId && chargerId.trim();
        if (!id) {
            alert('請輸入充電樁編號');
            return;
        }
        processQrCode(id);
    }, [chargerId, processQrCode]);

    // 測試掃描功能已移除（UI 精簡）

    return (
        <Wrapper bgTheme="green-blue">
            <div className={cx({ entry: true, loaded: true })}>
                <div className={styles.card}>
                    <div className={styles.logo}>
                        <img src={logo} alt="pick-n-charge-logo" />
                    </div>
                    <div>
                        <p style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            textAlign: 'center'
                        }}>
                            掃描充電槍 QR code
                        </p>

                        {/* QR 掃描器 */}
                        <div style={{
                            width: '100%',
                            maxWidth: '360px',
                            height: '260px',
                            margin: '8px auto 18px',
                            position: 'relative',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }} onClick={!isScanning ? toggleCamera : undefined}>
                            {isScanning ? (
                                <>
                                    {usingHtml5 ? (
                                        <div id="qr-reader" style={{ width: '100%', height: '100%' }} />
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            height: '100%',
                                            background: 'rgba(255, 0, 0, 0.1)',
                                            color: '#d32f2f',
                                            textAlign: 'center',
                                            padding: '20px'
                                        }}>
                                            <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 'bold' }}>
                                                掃描器無法啟動
                                            </p>
                                            <p style={{ margin: 0, fontSize: '14px' }}>
                                                Html5Qrcode 載入失敗，請重新整理頁面
                                            </p>
                                        </div>
                                    )}
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '220px',
                                        height: '140px',
                                        border: '3px solid rgba(0,255,200,0.85)',
                                        borderRadius: '10px',
                                        pointerEvents: 'none',
                                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)'
                                    }} />
                                    {/* 停止按鈕 */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCamera();
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px',
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            background: 'rgba(255, 0, 0, 0.8)',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.background = 'rgba(255, 0, 0, 1)';
                                            e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.background = 'rgba(255, 0, 0, 0.8)';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                    >
                                        ✕
                                    </button>
                                    <p style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        color: '#fff',
                                        fontSize: '13px',
                                        textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                                        margin: 0
                                    }}>
                                        或 輸入充電槍號
                                    </p>
                                </>
                            ) : (
                                /* 點擊啟動畫面 */
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #1e90ff 0%, #00ced1 100%)',
                                    color: 'white',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                        fontSize: '32px'
                                    }}>
                                        📷
                                    </div>
                                    <p style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        margin: '0 0 8px',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        點擊啟動掃描
                                    </p>
                                    <p style={{
                                        fontSize: '12px',
                                        margin: 0,
                                        opacity: 0.8
                                    }}>
                                        掃描充電槍 QR Code
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 錯誤訊息 */}
                        {error && (
                            <div className={styles.error}>
                                <div>{error}</div>
                            </div>
                        )}

                        {/* 載入中 */}
                        {loading && (
                            <div style={{
                                background: '#d1ecf1',
                                border: '1px solid #bee5eb',
                                borderRadius: '8px',
                                padding: '12px',
                                margin: '20px 0',
                                color: '#0c5460',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}>
                                處理中...
                            </div>
                        )}

                        {/* 掃描結果區已移除，UI 精簡 */}
                    </div>
                    <div>
                        <div style={{ margin: '8px 0 12px' }}>
                            <input
                                type="text"
                                placeholder="輸入充電槍編號 (AC-0001)"
                                value={chargerId}
                                onChange={(e) => setChargerId(e.target.value)}
                                style={{
                                    marginTop: '8px',
                                    padding: '12px 14px',
                                    width: '100%',
                                    borderRadius: '8px',
                                    border: '1px solid #eee',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <div className={styles.button}>
                            <Button onClick={connectCharging} disabled={loading}>
                                連接充電槍
                            </Button>
                        </div>
                    </div>
                </div>


            </div>
        </Wrapper>
    );
};

export default QrScannerPage;
